import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, of, throwError } from "rxjs";
import { catchError, map, tap, take } from "rxjs/operators";

import { ApiTokenService } from "@sharedV11/services/api/token/api-token.service";
import { GlobalService } from "@sharedV11/services/global/global.service";
import {
  AccessControlKuroi,
  AccessControlKuroiFilters,
  AccessControlKuroiListResponse,
  AccessControlKuroiPayload,
  AccessControlTipoControl,
  AccessControlTipoControlCalendario,
  AccessControlTipoControlCalendarioHorario,
  GetDatosListasResponse,
} from "@sharedV11/types/access-control-kuroi.model";
import { environment } from "src/environments/environment";
import { AccessControlSynchronizationService } from "./access-control-synchronization.service";

const JSON_HEADERS = new HttpHeaders({
  "Content-Type": "application/json",
});

@Injectable({
  providedIn: "root",
})
export class AccessControlKuroiService {
  private lugaresCache: { pkId: string; Nombre: string }[] = [];
  private centrosCache: { pkId: string; Nombre: string }[] = [];
  private readonly enableSynchronization = false;

  constructor(
    private http: HttpClient,
    private tokenService: ApiTokenService,
    private global: GlobalService,
    private syncService: AccessControlSynchronizationService
  ) {}

  getAccessControls(
    paginator,
    filters: AccessControlKuroiFilters[]
  ): Observable<AccessControlKuroiListResponse> {
    const { passthroughFilters, directFilterFields } =
      this.partitionFilters(filters);

    const body = this.buildBaseBody({
      paginator: paginator ?? { page: 1, pagelements: 25 },
      filters: passthroughFilters,
      ...directFilterFields,
    });

    return this.http
      .post<AccessControlKuroiListResponse>(
        this.getApiUrl("Customan/ObtenerCustomanAccesos"),
        JSON.stringify(body),
        { headers: JSON_HEADERS }
      )
      .pipe(
        map((response) => this.normalizeListResponse(response)),
        catchError(this.mapError)
      );
  }

  getAccesoKuroiById(
    pkId: string
  ): Observable<AccessControlKuroi | undefined> {
    const paginator = { page: 1, pagelements: 1 };
    const filters = [{ name: "pkId", value: pkId }];
    return this.getAccessControls(paginator, filters).pipe(
      map((response) => {
        const registros =
          response?.DatosResult?.customanAccesosKuroi ??
          response?.DatosResult?.customanAccesos ??
          [];
        const targetId = String(pkId ?? "");
        return registros.find((item) => {
          const currentId = String(
            (item as any)?.pkId ?? (item as any)?.PkId ?? ""
          );
          return currentId === targetId;
        });
      })
    );
  }

  insertAccesoKuroi(
    payload: AccessControlKuroiPayload
  ): Observable<any> {
    const body = this.buildMutationBody(payload, "I");
    return this.http
      .post(
        this.getApiUrl("Customan/InsertarActualizarCustomanAccesos"),
        JSON.stringify(body),
        { headers: JSON_HEADERS }
      )
      .pipe(
        map((response) => this.normalizeMutationResponse(response)),
        tap((response) => this.queueSynchronization(payload, response)),
        catchError(this.mapError)
      );
  }

  updateAccesoKuroi(
    payload: AccessControlKuroiPayload
  ): Observable<any> {
    const body = this.buildMutationBody(payload, "U");
    return this.http
      .post(
        this.getApiUrl("Customan/InsertarActualizarCustomanAccesos"),
        JSON.stringify(body),
        { headers: JSON_HEADERS }
      )
      .pipe(
        map((response) => this.normalizeMutationResponse(response)),
        tap((response) => this.queueSynchronization(payload, response)),
        catchError(this.mapError)
      );
  }

  deleteAccesoKuroi(clave: string): Observable<any> {
    const conexionIacpos =
      this.getConexionId() ??
      (this.global?.clienteapi as any)?.ConexionIacpos ??
      (this.global?.clienteapi as any)?.ConexionId ??
      "";

    const body: any = {
      ConexionIacpos: conexionIacpos?.toString?.() ?? conexionIacpos,
      clienteAPI: {
        Id: this.global?.clienteapi?.Id,
        Token: this.global?.clienteapi?.Token,
      },
      Clave: clave?.toString?.() ?? clave,
    };

    return this.http
      .post(
        this.getApiUrl("Customan/EliminarCustomanAccesos"),
        JSON.stringify(body),
        { headers: JSON_HEADERS }
      )
      .pipe(
        map((response) => this.normalizeMutationResponse(response)),
        tap((response) => this.queueSynchronization({}, response, "D")),
        catchError(this.mapError)
      );
  }

  getLugares(): Observable<{ pkId: string; Nombre: string }[]> {
    const params = new HttpParams()
      .set("NombreTabla", "lugares")
      .set("ConexionId", this.getConexionId());

    return this.http
      .get<GetDatosListasResponse>(
        `${this.tokenService.getConfig("API_MICROSERVICIOS")}/api/Maestros/GetDatosListas`,
        { params }
      )
      .pipe(
        map((response) => {
          const listas =
            response?.DatosResult?.DatosListas ??
            (response as any)?.datosResult?.datosListas ??
            [];
          const mapped: { pkId: string; Nombre: string }[] = listas.map(
            (item: any) => ({
              pkId: String(
                item?.pkId ??
                  item?.PkId ??
                  item?.pkID ??
                  item?.pkid ??
                  ""
              ),
              Nombre:
                item?.Nombre ??
                item?.nombre ??
                item?.Descripcion ??
                item?.descripcion ??
                "",
            })
          );
          const sorted = [...mapped].sort((a, b) =>
            a.Nombre.localeCompare(b.Nombre, undefined, { sensitivity: "base" })
          );
          this.lugaresCache = sorted;
          return sorted;
        }),
        catchError(() => of([]))
      );
  }

  getCentros(forceRefresh = false): Observable<{ pkId: string; Nombre: string }[]> {
    if (!forceRefresh && this.centrosCache.length) {
      return of(this.centrosCache);
    }

    const body = {
      clienteAPI: this.global.clienteapi,
      Nombre: "",
      Activo: "",
      Clave: "",
    };

    return this.http
      .post<any>(
        this.getApiUrl("Customan/ObtenerCentros"),
        JSON.stringify(body),
        { headers: JSON_HEADERS }
      )
      .pipe(
        map((response) => {
          const centrosRaw = response?.DatosResult?.ListaCentros ?? [];
          const mapped: { pkId: string; Nombre: string }[] = centrosRaw.map(
            (centro: any) => ({
              pkId: String(
                centro?.pkId ??
                  centro?.PkId ??
                  centro?.pkID ??
                  centro?.PKID ??
                  centro?.CentroId ??
                  ""
              ),
              Nombre:
                centro?.Nombre ??
                centro?.nombre ??
                centro?.Descripcion ??
                centro?.descripcion ??
                "",
            })
          );
          const sorted = [...mapped].sort((a, b) =>
            a.Nombre.localeCompare(b.Nombre, undefined, { sensitivity: "base" })
          );
          this.centrosCache = sorted;
          return sorted;
        }),
        catchError(() => of([]))
      );
  }

  getLugaresCached(): { pkId: string; Nombre: string }[] {
    return this.lugaresCache;
  }

  getLugarNombre(id: string | number): string | undefined {
    const lugares = this.getLugaresCached();
    if (!lugares?.length || id === undefined || id === null) {
      return undefined;
    }
    const idString = String(id);
    return lugares.find((lugar) => String(lugar.pkId) === idString)?.Nombre;
  }

  getTiposControlListado(): Observable<AccessControlTipoControl[]> {
    const body = {
      clienteAPI: this.global.clienteapi,
      paginator: {
        page: 1,
        pagelements: 300,
        orderby: "pkId",
        ordertype: "ASC",
      },
      filters: [],
      ConexionIacpos: this.getConexionId(),
      Usuario: this.getNombreUsuario(),
      Password: "",
    };

    return this.http
      .post<any>(
        this.getApiUrl("Customan/ObtenerCustomanTiposControl"),
        JSON.stringify(body),
        { headers: JSON_HEADERS }
      )
      .pipe(
        map(
          (response) =>
            response?.DatosResult?.customanTiposControl?.map(
              (tipo: any) => ({
                ...tipo,
                CompHoraria: this.parseBoolean(tipo.CompHoraria),
                PermisoSinFecha: this.parseBoolean(tipo.PermisoSinFecha),
                CompDiaria: this.parseBoolean(tipo.CompDiaria),
                CompIntervalo: this.parseBoolean(tipo.CompIntervalo),
                CompDiaSemana: this.parseBoolean(tipo.CompDiaSemana),
                CompPasoPrevio: this.parseBoolean(tipo.CompPasoPrevio),
                CompCalendario: this.parseBoolean(tipo.CompCalendario),
                CompCalendHorario: this.parseBoolean(tipo.CompCalendHorario),
                DenegarAcceso: this.parseBoolean(tipo.DenegarAcceso),
              })
            ) ?? []
        ),
        catchError(() => of([]))
      );
  }

  private partitionFilters(
    filters: AccessControlKuroiFilters[] | any[]
  ): {
    passthroughFilters: AccessControlKuroiFilters[];
    directFilterFields: Record<string, any>;
  } {
    const passthroughFilters: AccessControlKuroiFilters[] = [];
    const directFilterFields: Record<string, any> = {};

    if (!Array.isArray(filters)) {
      return { passthroughFilters, directFilterFields };
    }

    const directFieldMap: Record<string, string> = {
      pkid: "pkId",
      nombre: "Nombre",
      clave: "Clave",
      descripcion: "Descripcion",
      clase: "Clase",
      tipoacceso: "TipoAcceso",
      tipoconexion: "TipoConexion",
    };

    filters.forEach((rawFilter: any) => {
      const rawName =
        rawFilter?.name ??
        rawFilter?.db ??
        rawFilter?.translation ??
        rawFilter?.filterApi;
      const apiName = rawFilter?.filterApi ?? rawName;
      const value = rawFilter?.value;

      if (apiName === undefined || apiName === null) {
        return;
      }
      if (value === undefined || value === null || value === "") {
        return;
      }

      const normalized = apiName.toString().trim().toLowerCase();

      // Algunos filtros deben enviarse como campos de primer nivel en lugar de dentro de filters[]
      if (normalized === "tipoentsal") {
        directFilterFields.TipoEntSal = value;
        return;
      }
      if (normalized === "lugarid" || normalized === "lugar") {
        directFilterFields.LugarId = value;
        directFilterFields.Lugar = value;
        return;
      }
      if (normalized === "tipoconexion") {
        passthroughFilters.push({
          name: apiName,
          value: this.normalizeTipoConexionValue(value),
        });
        return;
      }

      const directFieldKey = directFieldMap[normalized];
      if (directFieldKey) {
        directFilterFields[directFieldKey] = value;
        return;
      }

      passthroughFilters.push({
        name: apiName,
        value,
      });
    });

    return { passthroughFilters, directFilterFields };
  }

  private buildBaseBody(extra: Record<string, any> = {}): any {
    const body: Record<string, any> = {
      ...extra,
    };

    if (!("clienteAPI" in body)) {
      body.clienteAPI = this.global.clienteapi;
    }

    const extraConexion =
      body.ConexionIacpos ??
      this.getConexionId() ??
      (this.tokenService.getConfig("ConexionIacpos") as string) ??
      (environment as any)?.CONEXION_IACPOS ??
      "";
    if (extraConexion && body.ConexionIacpos === undefined) {
      body.ConexionIacpos = extraConexion.toString();
    } else if (body.ConexionIacpos !== undefined) {
      body.ConexionIacpos = body.ConexionIacpos?.toString?.() ?? body.ConexionIacpos;
    }

    if (body.CodigoApp === undefined) {
      body.CodigoApp = this.getCodigoApp();
    }

    const usuario = this.getNombreUsuario();
    if (usuario) {
      body.Usuario ??= usuario;
      body.Password ??= "";
    }

    return body;
  }

  private buildMutationBody(
    payload: AccessControlKuroiPayload,
    funcion: "I" | "U"
  ): any {
    const body = this.buildBaseBody({
      Funcion: funcion,
    });

    // Para inserção (I), pkId debe ser "0". Para actualización (U), debe ser el ID del registro.
    if (funcion === "I") {
      body.pkId = "0";
    } else {
      body.pkId = payload.pkId ?? "";
    }
    body.Clave = payload.Clave ?? "";
    body.Nombre = payload.Nombre ?? "";
    body.Descripcion = payload.Descripcion ?? "";
    body.LugarId = payload.LugarId ?? payload.Lugar ?? "";
    body.Clase = payload.Clase ?? "";
    body.TipoAcceso = payload.TipoAcceso ?? "";
    body.TipoConexion = this.normalizeTipoConexionValue(
      payload.TipoConexion ?? ""
    );
    body.TipoEntSal = payload.TipoEntSal ?? "";
    body.DeBaja = this.flagFromValue(payload.DeBaja);
    body.EsSQLCentralizada = this.flagFromValue(payload.EsSQLCentralizada);
    // NombreProyecto no es requerido por la API InsertarActualizarCustomanAccesos;
    // se mantiene solo para uso interno en el front-end.
    
    // Los datos de conexión ahora se envían encriptados en JsonDatos
    // Los parámetros SQLServer, SQLDataBase, SQLUser, SQLPwd se envían como null
    // para compatibilidad, pero los datos reales están en JsonDatos encriptado
    body.JsonDatos = (payload as any).JsonDatos ?? "";
    body.SQLServer = null;
    body.SQLDataBase = null;
    body.SQLUser = null;
    body.SQLPwd = null;
    
    const configuraciones = payload.Configuraciones ?? {};
    const margenInferior = this.numberString(
      configuraciones.CHMargenInferior,
      "0"
    );
    const margenSuperior = this.numberString(
      configuraciones.CHMargenSuperior,
      "0"
    );
    const validacionEstandar = this.flagFromValue(
      configuraciones.ValidacionEstandar
    );
    const validacionInternet = this.flagFromValue(
      configuraciones.ValidacionInternet
    );
    const numPasosPrevios = this.numberString(
      configuraciones.NumPasosPrevios,
      "0"
    );
    const guardarFichajes = this.flagFromValue(
      configuraciones.GuardarFichajesIncorrectos
    );
    const permitirLoc = this.flagFromValue(
      configuraciones.PermitirLocInexistentes
    );

    body.MargenInferior = margenInferior;
    body.MargenSuperior = margenSuperior;
    body.ValidacionEstandar = validacionEstandar;
    body.ValidacionInternet = validacionInternet;
    body.NumPasosPrevios = numPasosPrevios;
    body.GuardarFichajesIncorrectos = guardarFichajes;
    body.PermitirLocInexistentes = permitirLoc;
    body.ListadoTiposControl = this.normalizeTiposControlForApi(
      (payload as any).ListadoTiposControl ??
        (payload as any).ListadoTiposcControl ??
        []
    );

    return body;
  }

  private normalizeTiposControlForApi(
    items: AccessControlTipoControl[]
  ): any[] {
    if (!Array.isArray(items)) {
      return [];
    }

    return items.map((tipo) => {
      const codigoTipoControl =
        tipo.CodigoTipoControl ?? (tipo as any)?.Codigo ?? "";

      return {
        CodigoTipoControl: codigoTipoControl,
      Nombre: tipo.Nombre ?? "",
      Descripcion: tipo.Descripcion ?? "",
      CompHoraria: this.flagFromValue(tipo.CompHoraria),
      PermisoSinFecha: this.flagFromValue(tipo.PermisoSinFecha),
      CompDiaria: this.flagFromValue(tipo.CompDiaria),
      CompIntervalo: this.flagFromValue(tipo.CompIntervalo),
      CompDiaSemana: this.flagFromValue(tipo.CompDiaSemana),
      CompPasoPrevio: this.flagFromValue(tipo.CompPasoPrevio),
      CompCalendario: this.flagFromValue(tipo.CompCalendario),
      CompCalendHorario: this.flagFromValue(tipo.CompCalendHorario),
      DenegarAcceso: this.flagFromValue(tipo.DenegarAcceso),
      PorDefecto: this.flagFromValue(tipo.PorDefecto),
      DiasIntervalo: this.numberString(tipo.DiasIntervalo),
      DiasIntervaloDesde: this.numberString(tipo.DiasIntervaloDesde),
      NumPasosPrevios: this.numberString(tipo.NumPasosPrevios, "1"),
      NumPasosPreviosDiarios: this.numberString(
        tipo.NumPasosPreviosDiarios,
        "1"
      ),
      NivelEntrada: (tipo.NivelEntrada ?? "1").toString(),
      AccesoLunes: this.flagFromValue(tipo.AccesoLunes),
      AccesoMartes: this.flagFromValue(tipo.AccesoMartes),
      AccesoMiercoles: this.flagFromValue(tipo.AccesoMiercoles),
      AccesoJueves: this.flagFromValue(tipo.AccesoJueves),
      AccesoViernes: this.flagFromValue(tipo.AccesoViernes),
      AccesoSabado: this.flagFromValue(tipo.AccesoSabado),
      AccesoDomingo: this.flagFromValue(tipo.AccesoDomingo),
        ListadoTiposControlCalendario: this.mapCalendario(
          tipo.ListadoTiposControlCalendario,
          codigoTipoControl,
          "fecha"
        ),
        ListadoTiposControlCalendarioHorario: this.mapCalendario(
          tipo.ListadoTiposControlCalendarioHorario,
          codigoTipoControl,
          "hora"
        ),
      };
    });
  }

  private mapCalendario(
    items?:
      | AccessControlTipoControlCalendario[]
      | AccessControlTipoControlCalendarioHorario[],
    codigo?: string,
    tipo: "fecha" | "hora" = "fecha"
  ): any[] {
    if (!Array.isArray(items)) {
      return [];
    }
    return items.map((item) => {
      const pkId =
        item?.pkId ??
        (item as any)?.PkId ??
        (item as any)?.pkID ??
        (item as any)?.pkid ??
        undefined;

      const base: any = {
        pkId,
        CodigoTipoControl: (item as any)?.CodigoTipoControl ?? codigo ?? "",
      };

      if (tipo === "hora") {
        const horaInicio =
          (item as any)?.HoraAcceso ?? (item as any)?.FechaAcceso ?? "";
        const horaFin =
          (item as any)?.HoraAccesoFin ?? (item as any)?.FechaAccesoFin ?? "";

        base.HoraAcceso = horaInicio ?? "";
        base.HoraAccesoFin = horaFin ?? "";
      } else {
        base.FechaAcceso = (item as any)?.FechaAcceso ?? "";
        base.FechaAccesoFin = (item as any)?.FechaAccesoFin ?? "";
      }

      return base;
    });
  }

  private flagFromValue(value: any): "1" | "0" {
    if (value === true || value === "true" || value === "True") {
      return "1";
    }
    if (value === false || value === "false" || value === "False") {
      return "0";
    }
    if (typeof value === "number") {
      return value !== 0 ? "1" : "0";
    }
    if (typeof value === "string") {
      const normalized = value.trim().toLowerCase();
      if (["1", "si", "sí", "true", "y", "yes"].includes(normalized)) {
        return "1";
      }
      if (["0", "no", "false"].includes(normalized)) {
        return "0";
      }
    }
    return value ? "1" : "0";
  }

  private numberString(value: any, fallback = "0"): string {
    if (value === undefined || value === null || value === "") {
      return fallback;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed.toString() : fallback;
  }

  private normalizeTipoConexionValue(value: any): string {
    const normalized = (value ?? "").toString().trim().toUpperCase();
    if (normalized === "REMOTO" || normalized === "REMOTA") {
      return "REMOTA";
    }
    if (normalized === "LOCAL") {
      return "LOCAL";
    }
    return normalized;
  }

  private getApiUrl(path: string): string {
    if (!path) {
      return "";
    }

    if (/^https?:\/\//i.test(path)) {
      return path;
    }

    const baseConfig =
      (this.tokenService.getConfig("API_URL") as string) ?? environment.API_URL ?? "";
    const normalizedBase = this.ensureTrailingSlash(baseConfig ?? "");
    const normalizedPath = this.trimLeadingSlash(path);

    if (/^\d+_/.test(normalizedPath)) {
      const originMatch = normalizedBase.match(/^(https?:\/\/[^/]+)/i);
      const origin = originMatch ? originMatch[1].replace(/\/+$/, "") : normalizedBase.replace(/\/+$/, "");
      return `${origin}/${normalizedPath}`;
    }

    return `${normalizedBase}${normalizedPath}`;
  }

  private ensureTrailingSlash(value: string): string {
    if (!value) {
      return "";
    }
    return value.endsWith("/") ? value : `${value}/`;
  }

  private trimLeadingSlash(value: string): string {
    if (!value) {
      return "";
    }
    return value.replace(/^\/+/, "");
  }

  private getCodigoApp(): string {
    return "KUROI";
  }

  private getConexionId(): string {
    return (
      localStorage.getItem("conexionIACPOS") ??
      sessionStorage.getItem("conexionIACPOS") ??
      (this.global?.clienteapi as any)?.ConexionIacpos ??
      (this.global?.clienteapi as any)?.ConexionId ??
      ""
    );
  }

  private getNombreUsuario(): string {
    try {
      const currentUser = JSON.parse(
        sessionStorage.getItem("currentUser") ?? "{}"
      );
      return currentUser?.DatosResult?.NombreUsuario ?? "";
    } catch {
      return "";
    }
  }

  private parseBoolean(value: boolean | string): boolean {
    if (typeof value === "boolean") {
      return value;
    }
    return value === "True" || value === "true" || value === "1";
  }

  private normalizeListResponse(
    response: AccessControlKuroiListResponse
  ): AccessControlKuroiListResponse {
    if (!response) {
      return { DatosResult: { customanAccesos: [] } };
    }

    const result = response.DatosResult ?? {};
    const registros =
      result.customanAccesos ??
      result.customanAccesosKuroi ??
      result.ListadoAccesos ??
      (result as any).listaAccesos ??
      (result as any).ListaAccesos ??
      [];

    const normalizedRecords = Array.isArray(registros)
      ? registros.map((item) => this.mapAccesoRecord(item))
      : [];

    return {
      ...response,
      DatosResult: {
        ...result,
        customanAccesos: normalizedRecords,
        customanAccesosKuroi: normalizedRecords,
        ListadoAccesos: normalizedRecords,
        listaAccesos: normalizedRecords,
      },
    };
  }

  private normalizeMutationResponse(response: any): any {
    if (!response?.DatosResult) {
      return response;
    }

    const result = response.DatosResult;
    if (result.customanAccesosKuroi && !result.customanAccesos) {
      response.DatosResult = {
        ...result,
        customanAccesos: result.customanAccesosKuroi,
      };
    }

    return response;
  }

  private mapAccesoRecord(record: any): any {
    if (!record) {
      return record;
    }

    record.ListadoTiposControl =
      record.ListadoTiposControl ?? record.ListadoTiposcControl ?? [];

    // Intentar desencriptar JsonDatos si existe
    let datosDesencriptados: any = null;
    const jsonDatos = record.JsonDatos;
    if (jsonDatos && typeof jsonDatos === "string" && jsonDatos.trim() !== "") {
      try {
        const jsonString = this.funcionDesencript(jsonDatos, this.NUEVA_CLAVE_PRIVADA);
        datosDesencriptados = JSON.parse(jsonString);
      } catch (error) {
        console.warn("[AccessControlKuroiService] Error al desencriptar JsonDatos:", error);
      }
    }

    // Si tenemos datos desencriptados, usarlos; si no, usar los valores existentes
    const sqlServer = datosDesencriptados?.a1 ?? record.SQLServer ?? "";
    const sqlDataBase = datosDesencriptados?.b9 ?? record.SQLDataBase ?? "";
    const sqlUser = datosDesencriptados?.u7 ?? record.SQLUser ?? "";
    const sqlPassword = datosDesencriptados?.p8 ?? record.SQLPwd ?? record.SQLPassword ?? "";

    if (!record.Conexion) {
      record.Conexion = {
        SQLServer: sqlServer,
        SQLDataBase: sqlDataBase,
        SQLUser: sqlUser,
        SQLPassword: sqlPassword,
        SQLPwd: sqlPassword,
      };
    }

    record.SQLServer = sqlServer;
    record.SQLDataBase = sqlDataBase;
    record.SQLUser = sqlUser;
    record.SQLPwd = sqlPassword;

    if (!record.Configuraciones) {
      record.Configuraciones = {
        Idioma: record.Idioma ?? "es-ES",
        CHMargenInferior: this.parseNumber(record.MargenInferior),
        CHMargenSuperior: this.parseNumber(record.MargenSuperior),
        ValidacionEstandar: this.flagFromValue(record.ValidacionEstandar),
        ValidacionInternet: this.flagFromValue(record.ValidacionInternet),
        NumPasosPrevios: this.parseNumber(record.NumPasosPrevios, 0),
        GuardarFichajesIncorrectos: this.flagFromValue(
          record.GuardarFichajesIncorrectos
        ),
        PermitirLocInexistentes: this.flagFromValue(
          record.PermitirLocInexistentes
        ),
      };
    }

    return record;
  }

  private parseNumber(value: any, fallback = 0): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  private queueSynchronization(
    payload: Partial<AccessControlKuroiPayload> | { pkId: string },
    response: any,
    mode: "I" | "U" | "D" = "U"
  ): void {
    if (!this.enableSynchronization) {
      return;
    }
    try {
      const syncPayload = this.buildSyncPayload(payload, response);
      if (!syncPayload) {
        return;
      }

      this.syncService
        .sync(syncPayload, response, mode)
        .pipe(take(1))
        .subscribe({
          next: () => this.invalidateCaches(mode),
          error: (error) =>
            console.warn(
              "[AccessControlKuroiService] Sincronización Kuroi fallida",
              error
            ),
        });
    } catch (error) {
      console.warn(
        "[AccessControlKuroiService] No se pudo preparar la sincronización Kuroi",
        error
      );
    }
  }

  private buildSyncPayload(
    payload: Partial<AccessControlKuroiPayload> | { pkId: string },
    response: any
  ): Partial<AccessControlKuroiPayload> | null {
    const pkId = this.resolvePkId(payload, response);
    if (!pkId) {
      return null;
    }

    const resultData = response?.DatosResult ?? {};
    const base: Partial<AccessControlKuroiPayload> = {
      ...(payload as Partial<AccessControlKuroiPayload>),
      pkId,
    };

    base.Nombre ??= resultData.Nombre;
    base.Clave ??= resultData.Clave;
    base.TipoAcceso ??= resultData.TipoAcceso;
    base.TipoConexion ??= resultData.TipoConexion;
    base.TipoEntSal ??= resultData.TipoEntSal;
    base.Clase ??= resultData.Clase;
    base.CentrosIds ??= resultData.CentrosIds;
    base.CentrosNombres ??= resultData.CentrosNombres;
    (base as any).ListadoTiposControl ??=
      resultData.ListadoTiposControl ??
      resultData.ListadoTiposcControl ??
      resultData.customanTiposcControl;
    base.NombreProyecto ??= resultData.NombreProyecto;
    base.EsSQLCentralizada ??= resultData.EsSQLCentralizada;
    base.Conexion ??= resultData.Conexion;

    return base;
  }

  private resolvePkId(
    payload: Partial<AccessControlKuroiPayload> | { pkId: string },
    response: any
  ): string | undefined {
    return (
      (payload as Partial<AccessControlKuroiPayload>)?.pkId ??
      response?.DatosResult?.pkId ??
      response?.DatosResult?.PkId ??
      response?.DatosResult?.id ??
      response?.pkId ??
      undefined
    );
  }

  private invalidateCaches(mode: "I" | "U" | "D"): void {
    if (mode !== "D") {
      this.lugaresCache = [];
    }
    this.centrosCache = [];
  }

  private sortByName<T extends { Nombre?: string }>(items: T[]): T[] {
    return [...items].sort((a, b) =>
      (a.Nombre || "").localeCompare(b.Nombre || "", undefined, {
        sensitivity: "base",
      })
    );
  }

  // Constante para la nueva clave privada de encriptación de datos de conexión
  private readonly NUEVA_CLAVE_PRIVADA = "3gdgdfhdhddhdhfs%i3535356kjfdsü&/@#$A·";

  // Funciones auxiliares para encriptación/desencriptación
  private Mid(strMid: string, intBeg: number, intEnd?: number): string {
    if (strMid === null || strMid === "" || intBeg < 0) {
      return "";
    }
    intBeg -= 1;
    if (intEnd === null || intEnd === undefined) {
      return strMid.substr(intBeg);
    } else {
      return strMid.substr(intBeg, intEnd);
    }
  }

  private Chr(num: number): string {
    return String.fromCharCode(num);
  }

  private Asc(str: string): number {
    return str.charCodeAt(0);
  }

  private Right(str: string, n: number): string {
    if (n <= 0) {
      return "";
    }
    const strValue = String(str);
    if (n >= strValue.length) {
      return strValue;
    }
    // Obtener los últimos n caracteres
    const iLen = strValue.length;
    return strValue.substring(iLen - n);
  }

  // Función de desencriptación que acepta la clave como parámetro
  private funcionDesencript(token: string, clavePrivada?: string): string {
    const clave = clavePrivada || "3ñdlfjdfksdffffs%i2343424kjfdsü&/@#$A·";
    let final = "";
    let j = 1;
    for (let index = 1; index <= token.length; index += 2) {
      const tokenMio = this.Mid(token, index, 2);
      const codigo = this.Mid(
        clave,
        ((j - 1) % clave.length) + 1,
        1
      );
      // tslint:disable-next-line:no-bitwise
      final =
        final + this.Chr(this.Asc(codigo) ^ parseInt("0x" + tokenMio, 16));
      j = j + 1;
    }
    return final;
  }

  private mapError = (error: any) => {
    return throwError(error);
  };
}

